import { VitalsRange } from "@/types/vitals";

// System defaults used whenever a member hasn't customized their range yet
// (TrackingConfig.ranges stays sparse until EditRangeModal's "custom range"
// flow is used) — without these, BP/Glucose readings would never flag as
// abnormal for members who never opened that screen.
export const DEFAULT_BP_RANGE: {
  systolic: [number, number];
  diastolic: [number, number];
} = {
  systolic: [90, 140],
  diastolic: [60, 90],
};

export const GLUCOSE_DEFAULT_RANGES: Partial<
  Record<keyof VitalsRange, [number, number]>
> = {
  fasting: [70, 99],
  postPrandial: [70, 139],
  random: [70, 139],
  beforeBed: [80, 140],
};

/**
 * The range a reading must be judged against.
 *
 * Per the vitals PRD, a reading keeps the classification it was given when it
 * was saved — editing a range must never retroactively re-flag history. The API
 * snapshots the range in force at save time onto each reading:
 * - a populated snapshot wins over the member's current range
 * - `{}` means no custom range existed then, so the system defaults apply
 *   (which is exactly what getBpRange/getGlucoseRange fall back to)
 * - readings predating the snapshot carry none, and fall back to the current
 *   range — the best guess available
 *
 * Reference bands and the chart's y-axis deliberately keep using the ACTIVE
 * range: the band shows today's target, the dots show how each reading was
 * judged at the time.
 */
export function getClassificationRange(
  reading: { recordedRange?: VitalsRange } | undefined,
  activeRange: VitalsRange | undefined
): VitalsRange | undefined {
  return reading?.recordedRange ?? activeRange;
}

export function getBpRange(range: VitalsRange | undefined): {
  systolic: [number, number];
  diastolic: [number, number];
} {
  return {
    systolic: range?.systolic ?? DEFAULT_BP_RANGE.systolic,
    diastolic: range?.diastolic ?? DEFAULT_BP_RANGE.diastolic,
  };
}

export function getGlucoseRange(
  category: string | undefined,
  range: VitalsRange | undefined
): [number, number] | undefined {
  const key = (
    category === "fasting" || category === "Fasting"
      ? "fasting"
      : category === "postPrandial" || category === "Post-prandial"
      ? "postPrandial"
      : category === "beforeBed" || category === "Before bed"
      ? "beforeBed"
      : "random"
  ) as keyof typeof GLUCOSE_DEFAULT_RANGES;
  const stored = range?.[key] as [number, number] | undefined;
  if (Array.isArray(stored) && stored.length >= 2) return stored;
  return GLUCOSE_DEFAULT_RANGES[key];
}
